import React, { useMemo, useRef, useEffect, useState } from 'react';
import DateScheduleView from './DateScheduleView';
import TeacherScheduleView from './TeacherScheduleView';
import GroupScheduleView from './GroupScheduleView';
import { useSupabaseData } from '@/hooks/useSupabaseData';

const ScheduleView = ({
  schedulesLoading,
  assignmentsLoading,
  viewMode,
  ViewModes,
  filteredSchedules,
  selectedGroup,
  selectedTeacher,
  teacherViewScope,
  TeacherViewScopes,
  selectedDate,
}) => {
  const lastStableTeacherSchedulesRef = useRef([]);
  const lastStableGroupSchedulesRef = useRef([]);
  const contentRef = useRef(null);
  const [lastGroupHeight, setLastGroupHeight] = useState(0);

  const isTeacherView = viewMode === ViewModes.BY_TEACHER && !!selectedTeacher;
  const isGroupView = viewMode === ViewModes.BY_GROUP && !!selectedGroup;

  const norm = (v) => String(v || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, '');
  const belongsToSelectedTeacher = (s) => norm(s?.teacher) === norm(selectedTeacher);
  const belongsToSelectedGroup = (s) => (s?.group_name || '').trim() === (selectedGroup || '').trim();

  const filteredArr = Array.isArray(filteredSchedules) ? filteredSchedules : [];

  const teacherDataReady =
    !schedulesLoading && !assignmentsLoading &&
    (!isTeacherView || filteredArr.length === 0 || filteredArr.every(belongsToSelectedTeacher));

  const groupDataReady =
    !schedulesLoading && !assignmentsLoading &&
    (!isGroupView || filteredArr.length === 0 || filteredArr.every(belongsToSelectedGroup));

  useEffect(() => {
    if (isTeacherView && teacherDataReady) {
      lastStableTeacherSchedulesRef.current = filteredArr;
    }
    if (isGroupView && !schedulesLoading) {
      lastStableGroupSchedulesRef.current = filteredArr;
    }
  }, [isTeacherView, teacherDataReady, isGroupView, schedulesLoading, filteredArr]);

  useEffect(() => {
    if (isGroupView) {
      requestAnimationFrame(() => {
        const h = contentRef.current?.offsetHeight || 0;
        if (h > 0) setLastGroupHeight(h);
      });
    }
  }, [isGroupView, groupDataReady, filteredArr]);

  const { data: groupModes } = useSupabaseData('group_modes', [], true);

  if (viewMode === ViewModes.BY_DATE) {
    return <DateScheduleView filteredSchedules={filteredArr} selectedDate={selectedDate} groupModes={groupModes} />;
  }

  if (viewMode === ViewModes.BY_TEACHER) {
    return (
      <TeacherScheduleView
        filteredArr={filteredArr}
        teacherDataReady={teacherDataReady}
        lastStableTeacherSchedulesRef={lastStableTeacherSchedulesRef}
        viewMode={viewMode}
        ViewModes={ViewModes}
        teacherViewScope={teacherViewScope}
        TeacherViewScopes={TeacherViewScopes}
        groupModes={groupModes}
        selectedDate={selectedDate}
        selectedTeacher={selectedTeacher}
      />
    );
  }

  if (viewMode === ViewModes.BY_GROUP) {
    return (
      <GroupScheduleView
        filteredArr={filteredArr}
        groupDataReady={groupDataReady}
        lastStableGroupSchedulesRef={lastStableGroupSchedulesRef}
        selectedGroup={selectedGroup}
        viewMode={viewMode}
        ViewModes={ViewModes}
        teacherViewScope={teacherViewScope}
        TeacherViewScopes={TeacherViewScopes}
        groupModes={groupModes}
        selectedDate={selectedDate}
      />
    );
  }

  return null;
};

export default ScheduleView;
